import { get, union, difference } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment, Grid, Header, Dropdown, Divider } from "semantic-ui-react";

import LinkWithReset from "../../utils/LinkWithReset.jsx";

import { ROUTES } from "../../../constants/routing.js";

import projectsSelector from "../../../state/reducers/projects/projectsSelector.js";
import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class ResourcePicker extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    formikProps: PropTypes.object,
    selectionLimit: PropTypes.number.isRequired,
    readOnly: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    violationErrors: PropTypes.object,
    resourcesByProject: PropTypes.array.isRequired,
    selectedResourceObjs: PropTypes.array.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    selectionLimit: -1,
    readOnly: false,
    disabled: false,
    copyKey: "forms.resource_picker"
  };

  constructor(props) {
    super(props);

    this.state = {
      projectAtId: null
    };
  }

  // -- CHANGE HANDLERS -- //

  onProjectChange = (evt, data) => {
    this.setState({ projectAtId: data.value });
  };

  onResourcesChange = (evt, data) => {
    const {
      id,
      selectionLimit,
      formikProps: { values, setFieldValue, setFieldTouched }
    } = this.props;

    const updatedResources = union(values[id], data.value);

    // abort if selection limit would be exceeded
    if (selectionLimit !== -1 && updatedResources.length > selectionLimit) {
      return null;
    }

    setFieldValue(id, updatedResources);
    setFieldTouched(id, true);
  };

  onSelectedResourcesChange = (evt, data) => {
    const {
      id,
      formikProps: { setFieldValue, setFieldTouched }
    } = this.props;

    setFieldValue(id, data.value);
    setFieldTouched(id, true);
  };

  // -- HELPERS -- //

  getOptionsFromData = data => {
    if (!data) return null;

    const result = data.map(item => {
      return { key: item["@id"], text: item.name, value: item["@id"] };
    });

    return result;
  };

  // -- RENDER -- //

  getProjects = () => {
    return this.getOptionsFromData(this.props.resourcesByProject);
  };

  getResources() {
    const { projectAtId } = this.state;
    const { resourcesByProject, selectedResourceObjs } = this.props;

    if (!resourcesByProject || !projectAtId) return null;

    const project = resourcesByProject.find(
      item => item["@id"] === projectAtId
    );

    const resources = difference(project.resources, selectedResourceObjs);

    return this.getOptionsFromData(resources);
  }

  getSelectedResources = () => {
    return this.getOptionsFromData(this.props.selectedResourceObjs);
  };

  renderReadOnly = selectedResources => {
    const resourceLinks = selectedResources.map((item, index) => {
      return (
        <span key={item.key}>
          {index > 0 && ", "}
          <LinkWithReset to={`${ROUTES.RESOURCES}${item.value}`}>
            {item.text}
          </LinkWithReset>
        </span>
      );
    });

    return <p>{resourceLinks}</p>;
  };

  render() {
    const {
      id,
      selectionLimit,
      formikProps: { values },
      readOnly,
      disabled,
      copyKey,
      copy
    } = this.props;

    // passing value={()} ro resources Dropdown so that multiselect is cleared on each render

    const noResources = !values || !values[id] || values[id].length < 1;
    const selectedResources = this.getSelectedResources();

    let titleCopyKey = "title_plural";
    let selectedCopyKey = "selected_plural";

    if (selectionLimit === 1) {
      titleCopyKey = "title";
      selectedCopyKey = "selected";
    }

    return (
      <Segment clearing={true}>
        <Header as="h3">{copy(`${copyKey}.${titleCopyKey}`)}</Header>
        {(readOnly || disabled) && noResources && (
          <p>{copy(`${copyKey}.no_resources`)}</p>
        )}
        {readOnly && this.renderReadOnly(selectedResources)}
        {!readOnly && (
          <Fragment>
            <Grid>
              <Grid.Row columns={3}>
                <Grid.Column>
                  {`${copy(`${copyKey}.projects`)}: `}
                  <br />
                  <Dropdown
                    selection
                    options={this.getProjects()}
                    onChange={this.onProjectChange}
                  />
                </Grid.Column>
                <Grid.Column>
                  {`${copy(`${copyKey}.resources`)}: `}
                  <br />
                  <Dropdown
                    selection
                    multiple
                    fluid
                    value={[]}
                    options={this.getResources()}
                    onChange={this.onResourcesChange}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={1}>
                <Grid.Column>
                  {`${copy(`${copyKey}.${selectedCopyKey}`)}: `}
                  <br />
                  <Dropdown
                    selection
                    multiple
                    fluid
                    readOnly={readOnly}
                    disabled={disabled}
                    value={values[id]}
                    options={selectedResources}
                    onChange={this.onSelectedResourcesChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Divider clearing hidden />
          </Fragment>
        )}
      </Segment>
    );
  }
}

const mapStateToProps = (state, props) => {
  const resources = get(props, `formikProps.values.${props.id}`, []);

  return {
    resourcesByProject: projectsSelector.selectResourcesList()(state),
    selectedResourceObjs:
      projectsSelector.selectResourcesByIds(resources)(state),
    copy: selectCopy(state)
  };
};

export default connect(mapStateToProps)(ResourcePicker);
